import { ChevronDown } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Space,
  Group,
  Text,
  LoadingOverlay,
} from "@mantine/core";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import EmployeeTemporaryBadge from "../elements/EmployeeTemporaryBadge";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import { dateOnlyFormatYY } from "../../../utils/DateTime";
import { AuthContext } from "../../../providers/AuthProvider";
import ErrorPage from "../../../common/pages/ErrorPage";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import useEmployeeListApiService from "../services/api/EmployeeListApiService";
import Pagination from "../../../themes/components/Pagination";
import EmployeeUploadForm from "./EmployeeUploadForm";
import Employee from "../services/model/Employee";
import EmployeeStatusBadge from "../elements/EmployeeStatusBadge";
import SyncPausedBadge from "../elements/SyncPausedBadge";
import EmployeeTableTagDropdown from "../elements/EmployeeTableTagDropdown";
import TagFilterIndicator from "./TagFilterIndicator";
import useTagDetailsApiService from "../../tags/services/api/TagDetailsApiService";
import useTagsListApiService from "../../tags/services/api/TagsListApiService";
import EmployeeTagChip from "../elements/EmployeeTagChip";
import EmployeeTableEmptyStateContainer from "./EmployeeTableEmptyStateContainer";
import EmployeeFilterForm from "./EmployeeFilterForm";
import SearchBar from "../../../themes/elements/SearchBar";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";
import EmployeeAuditLogButton from "../elements/EmployeeAuditLogButton";
import UserAccessControlUtils, {
  USER_ROLE_MAPPING,
  FEATURE_INTEGRATIONS,
} from "../../../utils/UserAccessControlUtils";

type EmployeeTableProps = {
  selection: Employee[];
  onSelection: React.Dispatch<React.SetStateAction<Employee[]>>;
};

const EmployeeTable: React.FC<EmployeeTableProps> = ({
  selection,
  onSelection,
}) => {
  const location = useLocation();
  const tagIdString = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tag");
  }, [location.search]);
  const tagId = tagIdString ? Number(tagIdString) : undefined;

  const isTemporaryParam = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const val = params.get("isTemporary");
    if (val === "true") return true;
    if (val === "false") return false;
    return undefined;
  }, [location.search]);
  const auth = useContext(AuthContext);
  const hasIntegrations = UserAccessControlUtils.hasFeature(
    auth?.user_features ?? [],
    FEATURE_INTEGRATIONS
  );
  const showAuditLog =
    auth?.user_role === USER_ROLE_MAPPING.ADMIN && hasIntegrations;
  const [activePage, setPage] = useState(1);
  const [opened, setOpened] = useState(false);
  const [multiTagSelectOpened, setMultiTagSelectOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  // Tag filter indicator hook MUST be called at the top
  const tagIdNumber = Number(tagId);
  const validTagId = !Number.isNaN(tagIdNumber) && !!tagId;
  const { data: tagDetails, isLoading: tagLoading } = useTagDetailsApiService(
    tagIdNumber,
    validTagId
  );

  const navigate = useNavigate();

  const tableMaxHeight = ResponsiveHeight(
    "",
    "70vh",
    "70vh",
    "70vh",
    "56vh",
    "70vh"
  );

  const { isLoading, isError, error, data, isFetching } =
    useEmployeeListApiService({
      employer: auth?.employer.id ?? 0,
      pagination: true,
      page: activePage,
      tsAndCsAccepted: false,
      search,
      tagId: tagId ?? undefined,
      slim: true,
      isTemporary: isTemporaryParam,
    });

  const {
    isLoading: isTagLoading,
    isError: isTagError,
    error: tagError,
    data: tagData,
  } = useTagsListApiService({});

  useEffect(() => {
    if (searchKeyword.length === 0) {
      setSearch("");
    }
  }, [searchKeyword]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (isLoading || isTagLoading) {
    return <ProgressIndicator />;
  }

  if (isError || isTagError) {
    return <ErrorPage errors={error || tagError} />;
  }

  if (!data) {
    return <EmployeeTableEmptyStateContainer />;
  }

  // Filtering is now handled by the backend if tagId is provided
  const filteredResults = data?.results;

  if (!filteredResults?.length && !search.length) {
    return <EmployeeTableEmptyStateContainer />;
  }

  const handleClearTagFilter = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete("tag");
    navigate(
      {
        pathname: location.pathname,
        search: url.search,
        hash: location.hash,
      },
      { replace: true }
    );
  };

  return (
    <>
      {/* Filtering logic: if tagId is provided, show only employees with that tag */}
      <Drawer
        opened={opened}
        size="xl"
        onClose={() => setOpened(false)}
        title="Import Employees"
        overlayOpacity={0.1}
        overlayBlur={3}
        position="right"
      >
        <div style={{ padding: "25px", height: "80%", overflow: "auto" }}>
          <EmployeeUploadForm onSuccess={() => setOpened(false)} />
        </div>
      </Drawer>
      <Space h="xl" />
      <div className="flex gap-sm items-center">
        <EmployeeFilterForm />
        <div className="w-[300px]">
          <SearchBar
            onSearch={() => setSearch(searchKeyword)}
            search={searchKeyword}
            onSearchChange={setSearchKeyword}
          />
        </div>
        {selection.length > 0 && (
          <Button
            variant="subtle"
            type="button"
            color="gray"
            rightIcon={<ChevronDown size={18} />}
            onClick={() => setMultiTagSelectOpened((value) => !value)}
          >
            Add/Remove Tags
          </Button>
        )}
      </div>
      <Space h="md" />

      {/* Tag filter indicator below search bar */}
      {validTagId && (
        <TagFilterIndicator
          tagName={tagDetails?.name}
          loading={tagLoading}
          onClear={handleClearTagFilter}
        />
      )}

      {multiTagSelectOpened && selection.length > 0 && (
        <>
          <Space h="xl" />
          <EmployeeTagChip
            employees={selection}
            activePage={activePage}
            tags={tagData || []}
          />
          <Space h="xl" />
        </>
      )}

      <LoadingOverlay visible={isFetching} />

      <DirectoryTable<Employee>
        tableMaxHeight={tableMaxHeight}
        data={filteredResults}
        selection={selection}
        onSelect={onSelection}
        headers={[
          "Employee No",
          "Full name",
          "Date added",
          "Mobile Number",
          "Tags",
          "Status",
          ...(showAuditLog ? ["Action"] : []),
        ]}
        rows={filteredResults.map((it) => ({
          employee_no: it.employee_no || "-",
          name: (
            <Group spacing="sm">
              <Text size="sm">{`${it.first_name} ${it.last_name[0]}.`}</Text>
              <EmployeeTemporaryBadge isTemporary={it.is_temporary} />
            </Group>
          ),
          dateCreated: dateOnlyFormatYY(it.date_created),
          mobile_number: it.mobile_number,
          tags:
            it.tags && it.tags.length > 0 ? (
              <EmployeeTableTagDropdown
                employeeId={it.id}
                assignedTags={it.tags}
                tags={tagData || []}
                employerId={auth.employer.id}
                activePage={activePage}
              />
            ) : (
              <Text size="sm">None</Text>
            ),
          status: (
            <Group spacing={4}>
              <EmployeeStatusBadge status={it.smartwage_status} />
              {hasIntegrations && (
                <SyncPausedBadge
                  syncPausedUntil={it.sync_paused_until}
                  syncPauseReason={it.sync_pause_reason}
                />
              )}
            </Group>
          ),
          ...(showAuditLog
            ? {
                action: (
                  <EmployeeAuditLogButton
                    employeeId={it.id}
                    employeeName={`${it.first_name} ${it.last_name}`}
                  />
                ),
              }
            : {}),
        }))}
      />

      {data && data.pages > 1 && (
        <Pagination onChange={setPage} total={data.pages} initialPage={1} />
      )}
    </>
  );
};

export default EmployeeTable;
